import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import {useNavigate} from "react-router-dom";
import {baseUrl} from "../Univ";
const VaccinationCertificate = ({
                                    patientId, administrationId,
                                }) => {
    const [administrations, setAdministrations] = useState([]);
    const navigate = useNavigate();
    const [certificates, setCertificates] = useState([]);
    const [selectedAdministration, setSelectedAdministration] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [form, setForm] = useState({
        certificate_number: "", certificate_date: new Date().toISOString().split("T")[0], status: "ISSUED", remarks: "",
    });
    const headers = () => ({
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    });
    /* ============================================================
       LOAD ADMINISTRATIONS + CERTIFICATES
       ============================================================ */
    useEffect(() => {
        loadData();
    }, [patientId]);
    const loadData = async () => {
        setLoading(true);
        setError("");
        try {
            const administrationParams = {};
            if (patientId) {
                administrationParams.patient = patientId;
            }
            const certificateParams = {};
            if (patientId) {
                certificateParams.patient = patientId;
            }
            const administrationResponse = await axios.get(`${baseUrl}vaccination/administrations/`, {
                params: administrationParams, headers: headers(),
            });
            const certificateResponse = await axios.get(`${baseUrl}vaccination/certificates/`, {
                params: certificateParams, headers: headers(),
            });
            const administrationData = Array.isArray(administrationResponse.data) ? administrationResponse.data : administrationResponse.data?.results || [];
            const certificateData = Array.isArray(certificateResponse.data) ? certificateResponse.data : certificateResponse.data?.results || [];
            setAdministrations(administrationData);
            setCertificates(certificateData);
            if (administrationId) {
                setSelectedAdministration(String(administrationId));
            } else if (administrationData.length > 0 && !selectedAdministration) {
                setSelectedAdministration(String(administrationData[0].id));
            }
        } catch (err) {
            console.error("Vaccination certificate loading error:", err);
            setError(err.response?.data?.detail || "Unable to load vaccination certificate data.");
        } finally {
            setLoading(false);
        }
    };
    /* ============================================================
       FORM CHANGE
       ============================================================ */
    const handleChange = (event) => {
        const {name, value} = event.target;
        setForm((previous) => ({
            ...previous, [name]: value,
        }));
    };
    /* ============================================================
       HELPERS
       ============================================================ */
    const getObjectId = (value) => {
        if (value && typeof value === "object") {
            return value.id || null;
        }
        return value || null;
    };
    const getPatient = (administration) => {
        if (administration?.patient && typeof administration.patient === "object") {
            return administration.patient;
        }
        return null;
    };
    const getVaccine = (administration) => {
        if (administration?.vaccine && typeof administration.vaccine === "object") {
            return administration.vaccine;
        }
        return null;
    };
    const getPatientName = (administration) => {
        const patient = getPatient(administration);
        return (patient?.patient || patient?.name || "-");
    };
    const getPatientNumber = (administration) => {
        const patient = getPatient(administration);
        return patient?.pat_id || "-";
    };
    const getVaccineName = (administration) => {
        const vaccine = getVaccine(administration);
        return (vaccine?.vaccine_name || vaccine?.name || administration?.vaccine_name || "-");
    };
    const getCertificatePatientName = (certificate) => {
        if (certificate?.patient && typeof certificate.patient === "object") {
            return (certificate.patient.patient || certificate.patient.name || "-");
        }
        return "-";
    };
    const getCertificateVaccineName = (certificate) => {
        if (certificate?.vaccine && typeof certificate.vaccine === "object") {
            return (certificate.vaccine.vaccine_name || certificate.vaccine.name || "-");
        }
        return "-";
    };
    /* ============================================================
       CREATE CERTIFICATE NUMBER
       ============================================================ */
    const createCertificateNumber = () => {
        const now = new Date();
        const year = now.getFullYear();
        const random = Math.floor(100000 + Math.random() * 900000);
        return `VC-${year}-${random}`;
    };
    /* ============================================================
       CREATE CERTIFICATE
       ============================================================ */
    const createCertificate = async () => {
        setError("");
        setMessage("");
        if (!selectedAdministration) {
            Swal.fire("Select Vaccination", "Please select a vaccination administration.", "warning");
            return;
        }
        const administration = administrations.find((item) => String(item.id) === String(selectedAdministration));
        if (!administration) {
            Swal.fire("Error", "Vaccination administration not found.", "error");
            return;
        }
        setSaving(true);
        try {
            const patientIdValue = getObjectId(administration.patient);
            const vaccineIdValue = getObjectId(administration.vaccine);
            const appointmentIdValue = getObjectId(administration.appointment);
            const scheduleIdValue = getObjectId(administration.schedule);
            const scheduleDoseIdValue = getObjectId(administration.schedule_dose);
            const certificateNumber = form.certificate_number.trim() || createCertificateNumber();
            const payload = {
                certificate_number: certificateNumber,
                patient: patientIdValue || patientId,
                administration: administration.id,
                appointment: appointmentIdValue,
                vaccine: vaccineIdValue,
                schedule: scheduleIdValue,
                schedule_dose: scheduleDoseIdValue,
                certificate_date: form.certificate_date,
                status: form.status,
                remarks: form.remarks,
            };
            console.log("Vaccination Certificate Payload:", payload);
            const response = await axios.post(`${baseUrl}vaccination/certificates/`, payload, {
                headers: {
                    ...headers(), "Content-Type": "application/json",
                },
            });
            await Swal.fire({
                icon: "success",
                title: "Certificate Created",
                text: response.data?.message || "Vaccination certificate created successfully.",
                confirmButtonText: "OK",
            });
            setMessage("Vaccination certificate created successfully.");
            setForm({
                certificate_number: "", certificate_date: new Date()
                    .toISOString()
                    .split("T")[0], status: "ISSUED", remarks: "",
            });
            await loadData();
        } catch (err) {
            console.error("Certificate creation error:", err);
            const backendError = err.response?.data;
            let errorMessage = "Unable to create vaccination certificate.";
            if (backendError && typeof backendError === "object") {
                errorMessage = Object.entries(backendError)
                    .map(([field, value]) => {
                        const text = Array.isArray(value) ? value.join(", ") : String(value);
                        return `${field}: ${text}`;
                    })
                    .join("\n");
            }
            setError(errorMessage);
            Swal.fire({
                icon: "error", title: "Certificate Failed", text: errorMessage,
            });
        } finally {
            setSaving(false);
        }
    };
    /* ============================================================
       SELECTED ADMINISTRATION RECORD
       ============================================================ */
    const selectedAdministrationRecord = administrations.find((item) => String(item.id) === String(selectedAdministration));
    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
        return (<Box
            sx={{
                minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center",
            }}
        >
            <CircularProgress/>
        </Box>);
    }
    /* ============================================================
       PAGE
       ============================================================ */
    return (<Box
        sx={{
            width: "100%", px: {
                xs: 1, sm: 2, md: 2.5,
            }, py: 2,
        }}
    >
        {/* ====================================================
               HEADER
               ==================================================== */}

        <Box sx={{mb: 3}}>
            <Typography
                sx={{
                    fontSize: {
                        xs: "21px", md: "24px",
                    }, fontWeight: 700, color: "#1e293b",
                }}
            >
                Vaccination Certificate
            </Typography>

            <Typography
                sx={{
                    mt: 0.5, fontSize: "13px", color: "#64748b",
                }}
            >
                Create and manage vaccination certificates
            </Typography>
        </Box>

        {message && (<Alert
            severity="success"
            sx={{
                mb: 2, borderRadius: "10px",
            }}
        >
            {message}
        </Alert>)}

        {error && (<Alert
            severity="error"
            sx={{
                mb: 2, borderRadius: "10px", whiteSpace: "pre-line",
            }}
        >
            {error}
        </Alert>)}

        {/* ====================================================
               CREATE CERTIFICATE
               ==================================================== */}

        <Card
            sx={{
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
                mb: 3,
            }}
        >
            <CardContent sx={{p: 2.5}}>
                <Typography
                    sx={{
                        fontSize: "16px", fontWeight: 700, color: "#1e293b",
                    }}
                >
                    Create Certificate
                </Typography>

                <Typography
                    sx={{
                        mt: 0.5, mb: 2, fontSize: "12px", color: "#94a3b8",
                    }}
                >
                    Select an administered vaccination
                    and create its certificate.
                </Typography>

                <Divider sx={{mb: 2.5}}/>

                <Grid
                    container
                    spacing={2}
                >
                    {/* ====================================================
                           ADMINISTRATION
                           ==================================================== */}

                    <Grid
                        item
                        xs={12}
                    >
                        <TextField
                            fullWidth
                            select
                            label="Vaccination Administration"
                            value={selectedAdministration}
                            onChange={(event) => setSelectedAdministration(event.target.value)}
                        >
                            {administrations.length === 0 ? (<MenuItem disabled>
                                No vaccination
                                administrations found
                            </MenuItem>) : (administrations.map((item) => (<MenuItem
                                key={item.id}
                                value={String(item.id)}
                            >
                                #{item.id} —{" "}
                                {getPatientName(item)} —{" "}
                                {getPatientNumber(item)} —{" "}
                                {getVaccineName(item)} — Dose{" "}
                                {item.dose_number || "-"}{" "}
                                —{" "}
                                {item.administered_date || "-"}
                            </MenuItem>)))}
                        </TextField>
                    </Grid>

                    {/* ====================================================
                           SELECTED ADMINISTRATION DETAILS
                           ==================================================== */}

                    {selectedAdministrationRecord && (<Grid
                        item
                        xs={12}
                    >
                        <Box
                            sx={{
                                p: 2, borderRadius: "10px", background: "#f8fafc", border: "1px solid #e2e8f0",
                            }}
                        >
                            <Grid
                                container
                                spacing={2}
                            >
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "11px", color: "#64748b",
                                        }}
                                    >
                                        Patient ID
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "14px", fontWeight: 700, color: "#1e293b",
                                        }}
                                    >
                                        {getPatientNumber(selectedAdministrationRecord)}
                                    </Typography>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "11px", color: "#64748b",
                                        }}
                                    >
                                        Patient
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "14px", fontWeight: 700, color: "#1e293b",
                                        }}
                                    >
                                        {getPatientName(selectedAdministrationRecord)}
                                    </Typography>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "11px", color: "#64748b",
                                        }}
                                    >
                                        Vaccine
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "14px", fontWeight: 700, color: "#1e293b",
                                        }}
                                    >
                                        {getVaccineName(selectedAdministrationRecord)}
                                    </Typography>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "11px", color: "#64748b",
                                        }}
                                    >
                                        Dose
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "14px", fontWeight: 700, color: "#1e293b",
                                        }}
                                    >
                                        {selectedAdministrationRecord.dose_number || "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>)}

                    {/* ====================================================
                           CERTIFICATE NUMBER
                           ==================================================== */}

                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <TextField
                            fullWidth
                            label="Certificate Number"
                            name="certificate_number"
                            value={form.certificate_number}
                            onChange={handleChange}
                            placeholder="Auto generated if blank"
                        />
                    </Grid>

                    {/* ====================================================
                           CERTIFICATE DATE
                           ==================================================== */}

                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <TextField
                            fullWidth
                            type="date"
                            label="Certificate Date"
                            name="certificate_date"
                            value={form.certificate_date}
                            onChange={handleChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>

                    {/* ====================================================
                           STATUS
                           ==================================================== */}

                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <TextField
                            fullWidth
                            select
                            label="Status"
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                        >
                            <MenuItem value="ISSUED">
                                ISSUED
                            </MenuItem>

                            <MenuItem value="ACTIVE">
                                ACTIVE
                            </MenuItem>

                            <MenuItem value="CANCELLED">
                                CANCELLED
                            </MenuItem>
                        </TextField>
                    </Grid>

                    {/* ====================================================
                           REMARKS
                           ==================================================== */}

                    <Grid
                        item
                        xs={12}
                    >
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Remarks"
                            name="remarks"
                            value={form.remarks}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* ====================================================
                           CREATE BUTTON
                           ==================================================== */}

                    <Grid
                        item
                        xs={12}
                    >
                        <Box
                            sx={{
                                display: "flex", justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={createCertificate}
                                disabled={saving || !selectedAdministration}
                                sx={{
                                    minWidth: 190,
                                    height: 44,
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    background: "#16a34a",
                                    "&:hover": {
                                        background: "#15803d",
                                    },
                                }}
                            >
                                {saving ? (<CircularProgress
                                    size={21}
                                    sx={{
                                        color: "#fff",
                                    }}
                                />) : (<>
                                    <i
                                        className="fa-solid fa-certificate"
                                        style={{
                                            marginRight: "8px",
                                        }}
                                    ></i>

                                    Create Certificate
                                </>)}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>

        {/* ====================================================
               EXISTING CERTIFICATES
               ==================================================== */}

        <Card
            sx={{
                borderRadius: "14px", border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
            }}
        >
            <CardContent sx={{p: 2.5}}>
                <Typography
                    sx={{
                        fontSize: "16px", fontWeight: 700, color: "#1e293b",
                    }}
                >
                    Existing Certificates
                </Typography>

                <Typography
                    sx={{
                        mt: 0.5, mb: 2, fontSize: "12px", color: "#94a3b8",
                    }}
                >
                    Certificates already issued for
                    this patient.
                </Typography>

                <Divider sx={{mb: 2}}/>

                {certificates.length === 0 ? (<Box
                    sx={{
                        py: 5, textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "14px", color: "#94a3b8",
                        }}
                    >
                        No vaccination certificates
                        found.
                    </Typography>
                </Box>) : (certificates.map((certificate) => (<Box
                    key={certificate.id}
                    sx={{
                        p: 2,
                        mb: 1.5,
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        background: "#ffffff",
                    }}
                >
                    <Grid
                        container
                        spacing={2}
                    >
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={2}
                        >
                            <Typography
                                sx={{
                                    fontSize: "11px", color: "#64748b",
                                }}
                            >
                                Certificate ID
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "14px", fontWeight: 700,
                                }}
                            >
                                #
                                {certificate.id}
                            </Typography>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                        >
                            <Typography
                                sx={{
                                    fontSize: "11px", color: "#64748b",
                                }}
                            >
                                Certificate
                                Number
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "14px", fontWeight: 700,
                                }}
                            >
                                <button
                                    type="button"
                                    className="btn btn-link p-0"
                                    onClick={() =>
                                        navigate(
                                            `/vaccination/certificate-print/${certificate.id}`
                                        )
                                    }
                                >
                                    {certificate.certificate_number || "-"}
                                </button>
                            </Typography>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={2}
                        >
                            <Typography
                                sx={{
                                    fontSize: "11px", color: "#64748b",
                                }}
                            >
                                Patient
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "13px",
                                }}
                            >
                                {getCertificatePatientName(certificate)}
                            </Typography>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={2}
                        >
                            <Typography
                                sx={{
                                    fontSize: "11px", color: "#64748b",
                                }}
                            >
                                Vaccine
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "13px",
                                }}
                            >
                                {getCertificateVaccineName(certificate)}
                            </Typography>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={1.5}
                        >
                            <Typography
                                sx={{
                                    fontSize: "11px", color: "#64748b",
                                }}
                            >
                                Date
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "13px",
                                }}
                            >
                                {certificate.certificate_date || "-"}
                            </Typography>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={1.5}
                        >
                            <Typography
                                sx={{
                                    fontSize: "11px", color: "#64748b",
                                }}
                            >
                                Status
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: certificate.status === "CANCELLED" ? "#dc2626" : "#16a34a",
                                }}
                            >
                                {certificate.status || "-"}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>)))}
            </CardContent>
        </Card>
    </Box>);
};
export default VaccinationCertificate;